import { useState, useEffect, useRef, useCallback } from "react";
import { getZone, formatPace, formatDuration, ZONE_COLORS } from "./RunDetail/utils";

// ─── Constants ────────────────────────────────────────────────────────────────
const _planStartRaw = new Date("2026-02-18");
const _dow = _planStartRaw.getDay();
const PLAN_START = new Date(_planStartRaw);
PLAN_START.setDate(_planStartRaw.getDate() + (_dow === 0 ? -6 : 1 - _dow));

const WEEK_PLAN = {
  1: { runs: 3, km: 7.5 },
  2: { runs: 3, km: 8.4 },
  3: { runs: 3, km: 9.9 },
  4: { runs: 3, km: 10.5 },
  5: { runs: 4, km: 16 },
  6: { runs: 4, km: 17 },
  7: { runs: 4, km: 18 },
  8: { runs: 4, km: 15 },
  9: { runs: 4, km: 22 },
  10: { runs: 4, km: 22 },
  11: { runs: 4, km: 22 },
  12: { runs: 4, km: 23 },
  13: { runs: 4, km: 24 },
  14: { runs: 4, km: 25 },
  15: { runs: 4, km: 20 },
  16: { runs: 4, km: 22 },
  17: { runs: 3, km: 11 },
  18: { runs: 3, km: 9 },
  19: { runs: 2, km: 4 },
};

function getWeekNumber(dateStr) {
  const days = Math.floor((new Date(dateStr) - PLAN_START) / 86400000);
  return Math.min(Math.max(Math.floor(days / 7) + 1, 1), 19);
}

function getWeekLabel(w) {
  const start = new Date(PLAN_START);
  start.setDate(PLAN_START.getDate() + (w - 1) * 7);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  const fmt = d => d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  return `W${w}: ${fmt(start)}–${fmt(end)}`;
}

// ─── Colour palette for comparison slots ─────────────────────────────────────
const COMPARE_COLORS = ["#E8650A", "#2E75B6", "#27AE60", "#9B59B6"];
const COMPARE_LABELS = ["A", "B", "C", "D"];

// ─── Shared helpers ───────────────────────────────────────────────────────────
function pctChange(a, b) {
  if (!a || !b || a === 0) return null;
  return ((b - a) / Math.abs(a)) * 100;
}

function DeltaBadge({ delta, invertGood = false }) {
  if (delta === null || isNaN(delta)) return null;
  const isGood = invertGood ? delta < 0 : delta > 0;
  const abs = Math.abs(delta).toFixed(1);
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: "bold",
        padding: "1px 5px",
        borderRadius: 6,
        background: isGood ? "#EDFAF1" : "#FDF0EF",
        color: isGood ? "#27AE60" : "#C0392B",
      }}
    >
      {delta > 0 ? "+" : ""}
      {abs}%
    </span>
  );
}

function SectionLabel({ children }) {
  return (
    <div
      style={{
        fontSize: 11,
        color: "var(--text-faint)",
        letterSpacing: 1.5,
        marginBottom: 10,
        fontFamily: "monospace",
        fontWeight: "bold",
      }}
    >
      {children}
    </div>
  );
}

// ─── SVG line chart that supports multiple series ─────────────────────────────
function MultiLineChart({ series, height = 100, yFormat, xLabels, showDots = false }) {
  const [hoverIdx, setHoverIdx] = useState(null);
  const ref = useRef(null);
  const maxLen = Math.max(...series.map(s => s.data.length), 1);

  const handleMove = useCallback(
    e => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const clientX = e.clientX ?? e.touches?.[0]?.clientX;
      const x = clientX - rect.left;
      setHoverIdx(Math.min(maxLen - 1, Math.max(0, Math.round((x / rect.width) * (maxLen - 1)))));
    },
    [maxLen]
  );

  const allValues = series.flatMap(s => s.data.filter(v => v != null && !isNaN(v)));
  if (!allValues.length)
    return (
      <div
        style={{
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--text-faint)",
          fontSize: 12,
        }}
      >
        No data
      </div>
    );

  const trueMin = Math.min(...allValues);
  const trueMax = Math.max(...allValues);
  const dataMin = trueMin * 0.97;
  const dataMax = trueMax * 1.03;
  const range = dataMax - dataMin || 1;

  // toY maps value → 0-100 viewBox (5–95 range, top = high values)
  function toY(v) {
    return 95 - ((v - dataMin) / range) * 90;
  }

  function buildPath(data) {
    const pts = data
      .map((v, i) => {
        if (v == null || isNaN(v)) return null;
        const x = (i / (data.length - 1)) * 100;
        return `${i === 0 ? "M" : "L"} ${x.toFixed(2)},${toY(v).toFixed(2)}`;
      })
      .filter(Boolean);
    return pts.join(" ");
  }

  const isHovering = hoverIdx !== null;

  return (
    <div style={{ position: "relative", paddingRight: 44 }}>
      {/* Static min/max labels — always visible on right */}
      <div
        style={{
          position: "absolute",
          right: 0,
          top: 0,
          bottom: xLabels ? 18 : 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          pointerEvents: "none",
          zIndex: 2,
        }}
      >
        <span style={{ fontSize: 9, color: "var(--text-faint)", fontFamily: "monospace", lineHeight: 1 }}>
          {yFormat ? yFormat(trueMax) : trueMax.toFixed(1)}
        </span>
        <span style={{ fontSize: 9, color: "var(--text-faint)", fontFamily: "monospace", lineHeight: 1 }}>
          {yFormat ? yFormat(trueMin) : trueMin.toFixed(1)}
        </span>
      </div>

      {/* Chart wrapper — position:relative so div dots can be placed correctly */}
      <div style={{ position: "relative" }}>
        <svg
          ref={ref}
          width="100%"
          height={height}
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          style={{ display: "block", cursor: "crosshair", overflow: "visible" }}
          onMouseMove={handleMove}
          onMouseLeave={() => setHoverIdx(null)}
          onTouchMove={e => {
            e.preventDefault();
            handleMove(e);
          }}
          onTouchEnd={() => setHoverIdx(null)}
        >
          {series.map((s, si) => (
            <path
              key={si}
              d={buildPath(s.data)}
              fill="none"
              stroke={s.color}
              strokeWidth="2.5"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
              opacity={0.9}
            />
          ))}
          {isHovering && (
            <line
              x1={(hoverIdx / (maxLen - 1)) * 100}
              y1="0"
              x2={(hoverIdx / (maxLen - 1)) * 100}
              y2="100"
              stroke="rgba(0,0,0,0.15)"
              strokeWidth="1"
              vectorEffect="non-scaling-stroke"
            />
          )}
        </svg>

        {/* Hover dots as divs — perfectly circular (SVG circles stretch with preserveAspectRatio=none) */}
        {isHovering &&
          showDots &&
          series.map((s, si) => {
            const idx = Math.min(hoverIdx, s.data.length - 1);
            const v = s.data[idx];
            if (v == null || isNaN(v)) return null;
            const xPct = (idx / (s.data.length - 1)) * 100;
            const yPct = toY(v); // 0-100 in viewBox space → convert to pixels
            return (
              <div
                key={si}
                style={{
                  position: "absolute",
                  left: `${xPct}%`,
                  top: `${(yPct / 100) * height}px`,
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  background: s.color,
                  border: "2px solid #fff",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
                  transform: "translate(-50%, -50%)",
                  pointerEvents: "none",
                  zIndex: 3,
                }}
              />
            );
          })}

        {/* Tooltip */}
        {isHovering && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: `${Math.min(74, Math.max(5, (hoverIdx / (maxLen - 1)) * 100))}%`,
              transform: "translateX(-50%)",
              background: "rgba(26,26,46,0.92)",
              color: "#fff",
              borderRadius: 8,
              padding: "6px 10px",
              pointerEvents: "none",
              fontSize: 11,
              fontFamily: "monospace",
              whiteSpace: "nowrap",
              boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
              zIndex: 10,
            }}
          >
            {series.map((s, si) => {
              const v = s.data[Math.min(hoverIdx, s.data.length - 1)];
              if (v == null || isNaN(v)) return null;
              return (
                <div key={si} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <div
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      background: s.color,
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ color: "rgba(255,255,255,0.7)" }}>{s.label}:</span>
                  <span style={{ fontWeight: "bold" }}>{yFormat ? yFormat(v) : v.toFixed(1)}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {xLabels && xLabels.length > 0 && (
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
          {xLabels.map((l, i) => (
            <span key={i} style={{ fontSize: 9, color: "var(--text-faint)", fontFamily: "monospace" }}>
              {l}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Radar chart (SVG) ────────────────────────────────────────────────────────
function RadarChart({ axes, series, size = 180 }) {
  const cx = size / 2,
    cy = size / 2,
    r = size * 0.38;
  const n = axes.length;
  const angleStep = (Math.PI * 2) / n;
  const getPoint = (angle, radius) => [
    cx + Math.sin(angle) * radius,
    cy - Math.cos(angle) * radius,
  ];

  const rings = [0.25, 0.5, 0.75, 1];

  return (
    <svg width={size} height={size} style={{ overflow: "visible" }}>
      {/* Grid rings */}
      {rings.map(ring => (
        <polygon
          key={ring}
          points={axes.map((_, i) => getPoint(i * angleStep, r * ring).join(",")).join(" ")}
          fill="none"
          stroke="var(--border)"
          strokeWidth="1"
        />
      ))}
      {/* Axis lines */}
      {axes.map((_, i) => {
        const [x, y] = getPoint(i * angleStep, r);
        return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke="var(--border)" strokeWidth="1" />;
      })}
      {/* Data polygons */}
      {series.map((s, si) => (
        <polygon
          key={si}
          points={s.values
            .map((v, i) => getPoint(i * angleStep, r * Math.min(1, v / 100)).join(","))
            .join(" ")}
          fill={s.color + "30"}
          stroke={s.color}
          strokeWidth="2"
          strokeLinejoin="round"
        />
      ))}
      {/* Axis labels */}
      {axes.map((axis, i) => {
        const [x, y] = getPoint(i * angleStep, r + 18);
        return (
          <text
            key={i}
            x={x}
            y={y}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="9"
            fill="var(--text-subtle)"
            fontFamily="monospace"
            fontWeight="bold"
          >
            {axis.label}
          </text>
        );
      })}
    </svg>
  );
}

// ─── Split heatmap ────────────────────────────────────────────────────────────
function buildSplits(streams_json, zoneConfig) {
  if (!streams_json) return [];
  try {
    const streams = JSON.parse(streams_json);
    const dist = streams?.distance?.data || [];
    const hr = streams?.heartrate?.data || [];
    const vel = streams?.velocity_smooth?.data || [];
    if (!dist.length) return [];
    const totalKm = Math.floor(dist[dist.length - 1] / 1000);
    const splits = [];
    for (let km = 1; km <= Math.min(totalKm, 12); km++) {
      const startIdx = dist.findIndex(d => d >= (km - 1) * 1000);
      const endIdx = dist.findIndex(d => d >= km * 1000);
      if (startIdx === -1 || endIdx === -1) break;
      const slice_hr = hr.slice(startIdx, endIdx).filter(Boolean);
      const slice_vel = vel.slice(startIdx, endIdx).filter(Boolean);
      const avgHr = slice_hr.length ? slice_hr.reduce((a, b) => a + b, 0) / slice_hr.length : null;
      const avgVel = slice_vel.length
        ? slice_vel.reduce((a, b) => a + b, 0) / slice_vel.length
        : null;
      const paceMinKm = avgVel > 0 ? 1000 / 60 / avgVel : null;
      splits.push({ km, avgHr, paceMinKm, zone: getZone(avgHr, zoneConfig) });
    }
    return splits;
  } catch {
    return [];
  }
}

function SplitHeatmap({ runs, stravaId, zoneConfig }) {
  const [fetchedStreams, setFetchedStreams] = useState({});
  const token = localStorage.getItem("auth_token");

  // Fetch streams for any run that doesn't have them
  useEffect(() => {
    runs.forEach(run => {
      if (!fetchedStreams[run.strava_id]) {
        fetch(`/api/runs/${stravaId}/${run.strava_id}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
          .then(r => r.json())
          .then(data => {
            setFetchedStreams(prev => ({ ...prev, [run.strava_id]: data.streams_json || null }));
          })
          .catch(() => {
            setFetchedStreams(prev => ({ ...prev, [run.strava_id]: null }));
          });
      }
    });
  }, [runs]);

  const runsWithSplits = runs.map(run => {
    const streams_json = run.streams_json || fetchedStreams[run.strava_id];
    return { ...run, splits: buildSplits(streams_json, zoneConfig) };
  });

  const loading = runs.some(r => !fetchedStreams[r.strava_id] && !r.streams_json);

  const maxKm = Math.max(...runsWithSplits.map(r => r.splits.length), 1);
  const kmCols = Array.from({ length: maxKm }, (_, i) => i + 1);
  const allEmpty = runsWithSplits.every(r => r.splits.length === 0);

  if (loading && allEmpty) {
    return (
      <div style={{ textAlign: "center", padding: 24, color: "var(--text-faint)", fontSize: 12 }}>
        Loading split data...
      </div>
    );
  }

  return (
    <div style={{ overflowX: "auto" }}>
      <div style={{ minWidth: 300 }}>
        <div style={{ display: "flex", gap: 2, marginBottom: 6 }}>
          <div style={{ width: 80, flexShrink: 0 }} />
          {kmCols.map(km => (
            <div
              key={km}
              style={{
                flex: 1,
                textAlign: "center",
                fontSize: 9,
                color: "var(--text-faint)",
                fontFamily: "monospace",
              }}
            >
              {km}km
            </div>
          ))}
        </div>
        {runsWithSplits.map((run, ri) => (
          <div key={ri} style={{ display: "flex", gap: 2, marginBottom: 3, alignItems: "center" }}>
            <div
              style={{
                width: 80,
                flexShrink: 0,
                fontSize: 10,
                color: run.color,
                fontWeight: "bold",
                fontFamily: "monospace",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {COMPARE_LABELS[ri]} W{run.week}
            </div>
            {kmCols.map(km => {
              const split = run.splits.find(s => s.km === km);
              const bg = split?.zone ? ZONE_COLORS[split.zone] : "var(--bg-subtle)";
              const opacity = split ? 1 : 0.15;
              return (
                <div
                  key={km}
                  title={
                    split
                      ? `HR: ${Math.round(split.avgHr || 0)} · ${split.paceMinKm ? formatPace(1 / (split.paceMinKm / 60)) : "—"}/km`
                      : "—"
                  }
                  style={{
                    flex: 1,
                    height: 28,
                    borderRadius: 4,
                    background: bg,
                    opacity,
                    cursor: split ? "help" : "default",
                  }}
                />
              );
            })}
          </div>
        ))}
        {/* Zone legend */}
        <div style={{ display: "flex", gap: 10, marginTop: 8, flexWrap: "wrap" }}>
          {[1, 2, 3, 4, 5].map(z => (
            <div key={z} style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <div style={{ width: 10, height: 10, borderRadius: 2, background: ZONE_COLORS[z] }} />
              <span style={{ fontSize: 10, color: "var(--text-subtle)" }}>Z{z}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Effort score trend ───────────────────────────────────────────────────────
function computeEffortScore(run) {
  if (!run.average_hr || !run.pace_min_per_km) return null;
  const maxHr = 195;
  const hrFraction = run.average_hr / maxHr;
  return (hrFraction * run.pace_min_per_km).toFixed(2);
}

// ─── Week comparison card ─────────────────────────────────────────────────────
function WeekCompareCard({ weekRuns, weekNum, color, label }) {
  const totalKm = weekRuns.reduce((s, r) => s + r.distance_km, 0);
  const avgPace = weekRuns
    .filter(r => r.pace_min_per_km)
    .reduce((s, r, _, a) => s + r.pace_min_per_km / a.length, 0);
  const avgHr = weekRuns
    .filter(r => r.average_hr)
    .reduce((s, r, _, a) => s + r.average_hr / a.length, 0);
  const z2pct = weekRuns.length
    ? Math.round((weekRuns.filter(r => r.zone2_compliant).length / weekRuns.length) * 100)
    : 0;
  const plan = WEEK_PLAN[weekNum] || {};

  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
        <div
          style={{
            width: 22,
            height: 22,
            borderRadius: "50%",
            background: color,
            color: "#fff",
            fontSize: 11,
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {label}
        </div>
        <span style={{ fontSize: 13, fontWeight: "bold", color: "var(--text-primary)" }}>Week {weekNum}</span>
        <span style={{ fontSize: 10, color: "var(--text-faint)" }}>{getWeekLabel(weekNum).split(": ")[1]}</span>
      </div>
      {[
        {
          label: "Runs",
          value: `${weekRuns.length}/${plan.runs || "—"}`,
          good: weekRuns.length >= (plan.runs || 0),
        },
        { label: "Distance", value: `${totalKm.toFixed(1)} km`, sub: `/ ${plan.km || "—"} km` },
        { label: "Avg Pace", value: avgPace ? formatPace(1 / (avgPace / 60)) : "—" },
        { label: "Avg HR", value: avgHr ? `${Math.round(avgHr)} bpm` : "—" },
        { label: "Zone 2", value: `${z2pct}%`, good: z2pct >= 80 },
      ].map((item, i) => (
        <div
          key={i}
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            padding: "5px 0",
            borderBottom: "1px solid var(--border-subtle)",
          }}
        >
          <span style={{ fontSize: 11, color: "var(--text-subtle)" }}>{item.label}</span>
          <div style={{ textAlign: "right" }}>
            <span
              style={{
                fontSize: 13,
                fontWeight: "bold",
                color: item.good === true ? "#27AE60" : item.good === false ? "#C0392B" : "var(--text-primary)",
              }}
            >
              {item.value}
            </span>
            {item.sub && (
              <span style={{ fontSize: 10, color: "var(--text-faint)", marginLeft: 3 }}>{item.sub}</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Run overlay chart ────────────────────────────────────────────────────────
function RunOverlayChart({ selectedRuns, stravaId }) {
  const [mode, setMode] = useState("hr");
  const [runStreams, setRunStreams] = useState({});
  const token = localStorage.getItem("auth_token");

  useEffect(() => {
    selectedRuns.forEach(run => {
      if (!runStreams[run.strava_id]) {
        fetch(`/api/runs/${stravaId}/${run.strava_id}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
          .then(r => r.json())
          .then(data => {
            if (data.streams_json) {
              setRunStreams(prev => ({ ...prev, [run.strava_id]: JSON.parse(data.streams_json) }));
            }
          });
      }
    });
  }, [selectedRuns]);

  const MODES = [
    { key: "hr", label: "Heart Rate", format: v => `${Math.round(v)} bpm` },
    {
      key: "pace",
      label: "Pace",
      format: v => {
        const m = Math.floor(v);
        const s = Math.round((v - m) * 60);
        return `${m}:${s.toString().padStart(2, "0")}/km`;
      },
    },
    { key: "altitude", label: "Elevation", format: v => `${Math.round(v)}m` },
  ];

  const series = selectedRuns
    .map((run, i) => {
      const streams = runStreams[run.strava_id];
      if (!streams) return null;
      let data = [];
      if (mode === "hr") data = streams.heartrate?.data || [];
      if (mode === "pace")
        data = (streams.velocity_smooth?.data || [])
          .map(v => (v > 0 ? 1000 / 60 / v : null))
          .filter(Boolean);
      if (mode === "altitude") data = streams.altitude?.data || [];
      // Downsample to ~200 points for perf
      if (data.length > 200) {
        const step = Math.floor(data.length / 200);
        data = data.filter((_, idx) => idx % step === 0);
      }
      const distData = streams.distance?.data || [];
      const totalKm = distData.length ? distData[distData.length - 1] / 1000 : 0;
      return {
        label: `${COMPARE_LABELS[i]} W${run.week} (${run.distance_km.toFixed(1)}km)`,
        data,
        color: COMPARE_COLORS[i],
        totalKm,
      };
    })
    .filter(Boolean);

  const currentMode = MODES.find(m => m.key === mode);

  return (
    <div>
      {/* Mode tabs */}
      <div
        style={{
          display: "flex",
          gap: 4,
          marginBottom: 14,
          background: "var(--bg-subtle)",
          borderRadius: 8,
          padding: 3,
        }}
      >
        {MODES.map(m => (
          <button
            key={m.key}
            onClick={() => setMode(m.key)}
            style={{
              flex: 1,
              padding: "6px 4px",
              border: "none",
              borderRadius: 6,
              cursor: "pointer",
              background: mode === m.key ? "var(--bg-card)" : "transparent",
              color: mode === m.key ? "var(--text-primary)" : "var(--text-subtle)",
              fontSize: 12,
              fontFamily: "'Georgia', serif",
              fontWeight: mode === m.key ? "bold" : "normal",
              boxShadow: mode === m.key ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
            }}
          >
            {m.label}
          </button>
        ))}
      </div>

      {series.length === 0 ? (
        <div style={{ textAlign: "center", padding: 30, color: "var(--text-faint)", fontSize: 13 }}>
          Loading stream data...
        </div>
      ) : (
        <div style={{ background: "var(--bg-card)", borderRadius: 12, padding: 14 }}>
          <MultiLineChart series={series} height={120} yFormat={currentMode.format} showDots />
          {/* Legend */}
          <div style={{ display: "flex", gap: 12, marginTop: 10, flexWrap: "wrap" }}>
            {series.map((s, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <div style={{ width: 14, height: 3, background: s.color, borderRadius: 2 }} />
                <span style={{ fontSize: 10, color: "var(--text-muted)" }}>{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Fitness fingerprint radar ────────────────────────────────────────────────
function computeFingerprint(runs, allRuns) {
  if (!runs.length || !allRuns.length) return null;

  // Score each dimension 0–100 relative to all runs
  const allPaces = allRuns.map(r => r.pace_min_per_km).filter(Boolean);
  const allHrs = allRuns.map(r => r.average_hr).filter(Boolean);

  function percentile(val, arr, invertLower = false) {
    if (!val || !arr.length) return 50;
    const sorted = [...arr].sort((a, b) => a - b);
    const rank = sorted.filter(v => v < val).length;
    const pct = (rank / sorted.length) * 100;
    return invertLower ? 100 - pct : pct;
  }

  const avgPace = runs
    .map(r => r.pace_min_per_km)
    .filter(Boolean)
    .reduce((a, b, _, arr) => a + b / arr.length, 0);
  const avgHr = runs
    .map(r => r.average_hr)
    .filter(Boolean)
    .reduce((a, b, _, arr) => a + b / arr.length, 0);
  const z2pct = runs.length ? (runs.filter(r => r.zone2_compliant).length / runs.length) * 100 : 0;
  const completionPct = 100; // all selected runs are completed
  const totalKm = runs.reduce((s, r) => s + r.distance_km, 0);
  const allWeekKm = Object.values(WEEK_PLAN).map(w => w.km);

  return [
    { score: Math.round(percentile(avgPace, allPaces, true)), label: "PACE" },
    { score: Math.round(100 - percentile(avgHr, allHrs)), label: "HR EFF." }, // lower HR = better
    { score: Math.round(z2pct), label: "Z2 COMP." },
    { score: Math.min(100, Math.round(completionPct)), label: "CONSIST." },
    { score: Math.min(100, Math.round((totalKm / (allWeekKm[0] || 1)) * 20)), label: "VOLUME" },
    {
      score: Math.max(
        0,
        Math.round(
          100 - Math.abs(avgPace - allPaces.reduce((a, b) => a + b, 0) / allPaces.length) * 10
        )
      ),
      label: "RHYTHM",
    },
  ];
}

// ─── Main Compare component ───────────────────────────────────────────────────
export default function Compare({ stravaId, allRuns: allRunsProp, zoneConfig }) {
  const [compareMode, setCompareMode] = useState("weeks");
  const [selectedWeeks, setSelectedWeeks] = useState([]);
  const [selectedRuns, setSelectedRuns] = useState([]);
  const [activeSection, setActiveSection] = useState("summary");
  const [fetchedRuns, setFetchedRuns] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch own data if parent hasn't provided it yet (e.g. navigated directly to Compare)
  useEffect(() => {
    if (!stravaId) return;
    if (allRunsProp && allRunsProp.length > 0) return;
    setLoading(true);
    fetch(`/api/progress/${stravaId}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("auth_token")}` },
    })
      .then(r => r.json())
      .then(data => setFetchedRuns(data.runs || []))
      .catch(() => setFetchedRuns([]))
      .finally(() => setLoading(false));
  }, [stravaId, allRunsProp]);

  const allRuns = allRunsProp && allRunsProp.length > 0 ? allRunsProp : fetchedRuns || [];

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--text-subtle)",
          fontFamily: "'Georgia', serif",
        }}
      >
        Loading runs...
      </div>
    );
  }

  // Use week from API directly (already correctly calculated server-side).
  // Fallback to local calc only if missing. Also ensure distance_km is numeric.
  const runsWithMeta = allRuns.map(r => ({
    ...r,
    distance_km: parseFloat(r.distance_km) || 0,
    week: r.week || getWeekNumber(r.date),
  }));

  // Available weeks that have data
  const weeksWithData = [...new Set(runsWithMeta.map(r => r.week))].sort((a, b) => a - b);

  const MAX_COMPARE = 4;

  function toggleWeek(w) {
    setSelectedWeeks(prev =>
      prev.includes(w) ? prev.filter(x => x !== w) : prev.length < MAX_COMPARE ? [...prev, w] : prev
    );
  }

  function toggleRun(run) {
    setSelectedRuns(prev =>
      prev.find(r => r.strava_id === run.strava_id)
        ? prev.filter(r => r.strava_id !== run.strava_id)
        : prev.length < MAX_COMPARE
          ? [...prev, run]
          : prev
    );
  }

  const selectedWeekRuns = selectedWeeks.map(w => runsWithMeta.filter(r => r.week === w));
  const comparedRuns =
    compareMode === "weeks"
      ? selectedWeeks.map((w, i) => ({
          week: w,
          color: COMPARE_COLORS[i],
          label: COMPARE_LABELS[i],
          runs: selectedWeekRuns[i],
        }))
      : selectedRuns.map((r, i) => ({ ...r, color: COMPARE_COLORS[i], label: COMPARE_LABELS[i] }));

  const hasSelection =
    compareMode === "weeks" ? selectedWeeks.length >= 2 : selectedRuns.length >= 2;

  // Build trend data for all runs (sorted by date)
  const sortedRuns = [...runsWithMeta].sort((a, b) => new Date(a.date) - new Date(b.date));
  const effortTrend = sortedRuns
    .map(r => ({ ...r, effort: computeEffortScore(r) }))
    .filter(r => r.effort);

  const SECTIONS = [
    { key: "summary", label: "Summary" },
    { key: "overlay", label: "Chart Overlay" },
    { key: "splits", label: "Split Heatmap" },
    { key: "radar", label: "Fitness Radar" },
    { key: "trends", label: "Trends" },
  ];

  return (
    <div
      style={{
        fontFamily: "'Georgia', serif",
        background: "var(--bg-page)",
        minHeight: "100vh",
        paddingBottom: 60,
      }}
    >
      {/* Header */}
      <div style={{ background: "#1A1A2E", padding: "20px 20px 0" }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <div
            style={{
              color: "rgba(255,255,255,0.5)",
              fontSize: 11,
              letterSpacing: 2,
              fontFamily: "monospace",
              marginBottom: 4,
            }}
          >
            COMPARE
          </div>
          <div style={{ color: "#fff", fontSize: 18, fontWeight: "bold", marginBottom: 16 }}>
            Run Analysis
          </div>

          {/* Week / Run toggle */}
          <div style={{ display: "flex", gap: 0, marginBottom: 0 }}>
            {["weeks", "runs"].map(mode => (
              <button
                key={mode}
                onClick={() => {
                  setCompareMode(mode);
                  setSelectedWeeks([]);
                  setSelectedRuns([]);
                }}
                style={{
                  flex: 1,
                  padding: "9px 14px",
                  border: "none",
                  borderBottom:
                    compareMode === mode ? "3px solid #E8650A" : "3px solid transparent",
                  background: "transparent",
                  color: compareMode === mode ? "#fff" : "rgba(255,255,255,0.4)",
                  fontFamily: "'Georgia', serif",
                  fontSize: 13,
                  cursor: "pointer",
                  fontWeight: compareMode === mode ? "bold" : "normal",
                }}
              >
                {mode === "weeks" ? "Week vs Week" : "Run vs Run"}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div style={{ padding: "20px 16px", maxWidth: 720, margin: "0 auto" }}>
        {/* ── Selection panel ── */}
        <div
          style={{
            background: "var(--bg-card)",
            border: "1.5px solid var(--border)",
            borderRadius: 12,
            padding: "14px 16px",
            marginBottom: 16,
          }}
        >
          <SectionLabel>
            {compareMode === "weeks"
              ? `SELECT WEEKS TO COMPARE (${selectedWeeks.length}/${MAX_COMPARE})`
              : `SELECT RUNS TO COMPARE (${selectedRuns.length}/${MAX_COMPARE})`}
          </SectionLabel>

          {compareMode === "weeks" ? (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {weeksWithData.map(w => {
                const idx = selectedWeeks.indexOf(w);
                const isSelected = idx !== -1;
                const color = isSelected ? COMPARE_COLORS[idx] : null;
                return (
                  <button
                    key={w}
                    onClick={() => toggleWeek(w)}
                    style={{
                      padding: "5px 12px",
                      borderRadius: 8,
                      cursor: "pointer",
                      border: `2px solid ${isSelected ? color : "var(--border)"}`,
                      background: isSelected ? color : "var(--bg-card)",
                      color: isSelected ? "#fff" : "var(--text-muted)",
                      fontSize: 12,
                      fontFamily: "'Georgia', serif",
                      fontWeight: isSelected ? "bold" : "normal",
                      transition: "all 0.15s",
                    }}
                  >
                    {isSelected && (
                      <span style={{ marginRight: 5, opacity: 0.8 }}>{COMPARE_LABELS[idx]}</span>
                    )}
                    W{w}
                  </button>
                );
              })}
              {weeksWithData.length === 0 && (
                <div style={{ color: "var(--text-faint)", fontSize: 13 }}>
                  No weeks with data yet. Complete some runs first.
                </div>
              )}
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 4,
                maxHeight: 220,
                overflowY: "auto",
              }}
            >
              {sortedRuns.map((run, i) => {
                const selIdx = selectedRuns.findIndex(r => r.strava_id === run.strava_id);
                const isSelected = selIdx !== -1;
                const color = isSelected ? COMPARE_COLORS[selIdx] : null;
                return (
                  <div
                    key={i}
                    onClick={() => toggleRun(run)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "8px 10px",
                      borderRadius: 8,
                      cursor: "pointer",
                      border: `1.5px solid ${isSelected ? color : "var(--border-subtle)"}`,
                      background: isSelected ? color + "12" : "var(--bg-page)",
                      transition: "all 0.15s",
                    }}
                  >
                    {isSelected && (
                      <div
                        style={{
                          width: 18,
                          height: 18,
                          borderRadius: "50%",
                          background: color,
                          color: "#fff",
                          fontSize: 10,
                          fontWeight: "bold",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        {COMPARE_LABELS[selIdx]}
                      </div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: "bold",
                          color: "var(--text-primary)",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {run.name}
                      </div>
                      <div style={{ fontSize: 10, color: "var(--text-faint)" }}>
                        W{run.week} ·{" "}
                        {new Date(run.date).toLocaleDateString("en-GB", {
                          day: "numeric",
                          month: "short",
                        })}{" "}
                        · {run.distance_km} km
                      </div>
                    </div>
                    <div style={{ fontSize: 10, color: "var(--text-faint)", flexShrink: 0 }}>
                      {run.average_hr ? `${Math.round(run.average_hr)} bpm` : ""}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Comparison legend ── */}
        {hasSelection && (
          <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
            {comparedRuns.map((item, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  background: "var(--bg-card)",
                  border: `1.5px solid ${item.color}`,
                  borderRadius: 8,
                  padding: "4px 10px",
                }}
              >
                <div
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    background: item.color,
                    color: "#fff",
                    fontSize: 10,
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {item.label}
                </div>
                <span style={{ fontSize: 11, fontWeight: "bold", color: item.color }}>
                  {compareMode === "weeks" ? `Week ${item.week}` : item.name}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* ── Section tabs ── */}
        {hasSelection && (
          <>
            <div
              style={{
                display: "flex",
                gap: 2,
                background: "var(--bg-subtle)",
                borderRadius: 10,
                padding: 3,
                marginBottom: 20,
                overflowX: "auto",
              }}
            >
              {SECTIONS.map(s => (
                <button
                  key={s.key}
                  onClick={() => setActiveSection(s.key)}
                  style={{
                    flex: "0 0 auto",
                    padding: "7px 12px",
                    border: "none",
                    borderRadius: 7,
                    cursor: "pointer",
                    background: activeSection === s.key ? "var(--bg-card)" : "transparent",
                    color: activeSection === s.key ? "var(--text-primary)" : "var(--text-subtle)",
                    fontSize: 12,
                    fontFamily: "'Georgia', serif",
                    fontWeight: activeSection === s.key ? "bold" : "normal",
                    boxShadow: activeSection === s.key ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  {s.label}
                </button>
              ))}
            </div>

            {/* ══ SUMMARY ══ */}
            {activeSection === "summary" && (
              <div>
                {compareMode === "weeks" ? (
                  <>
                    {/* Side-by-side week stats */}
                    <div
                      style={{
                        background: "var(--bg-card)",
                        borderRadius: 12,
                        padding: 16,
                        marginBottom: 16,
                      }}
                    >
                      <SectionLabel>WEEK STATS</SectionLabel>
                      <div style={{ display: "flex", gap: 16 }}>
                        {comparedRuns.map((item, i) => (
                          <WeekCompareCard
                            key={i}
                            weekRuns={item.runs}
                            weekNum={item.week}
                            color={item.color}
                            label={item.label}
                          />
                        ))}
                      </div>
                    </div>

                    {/* Delta summary — only for exactly 2 weeks */}
                    {selectedWeeks.length === 2 &&
                      (() => {
                        const [wa, wb] = comparedRuns;
                        const aKm = wa.runs.reduce((s, r) => s + r.distance_km, 0);
                        const bKm = wb.runs.reduce((s, r) => s + r.distance_km, 0);
                        const aPace = wa.runs
                          .filter(r => r.pace_min_per_km)
                          .reduce((s, r, _, a) => s + r.pace_min_per_km / a.length, 0);
                        const bPace = wb.runs
                          .filter(r => r.pace_min_per_km)
                          .reduce((s, r, _, a) => s + r.pace_min_per_km / a.length, 0);
                        const aHr = wa.runs
                          .filter(r => r.average_hr)
                          .reduce((s, r, _, a) => s + r.average_hr / a.length, 0);
                        const bHr = wb.runs
                          .filter(r => r.average_hr)
                          .reduce((s, r, _, a) => s + r.average_hr / a.length, 0);

                        const metrics = [
                          { label: "Distance", delta: pctChange(aKm, bKm), invertGood: false },
                          { label: "Avg Pace", delta: pctChange(aPace, bPace), invertGood: true },
                          { label: "Avg HR", delta: pctChange(aHr, bHr), invertGood: true },
                        ];

                        return (
                          <div
                            style={{
                              background: "var(--bg-card)",
                              borderRadius: 12,
                              padding: 16,
                              marginBottom: 16,
                            }}
                          >
                            <SectionLabel>
                              WEEK {wb.week} vs WEEK {wa.week}
                            </SectionLabel>
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(3,1fr)",
                                gap: 10,
                              }}
                            >
                              {metrics.map((m, i) => (
                                <div
                                  key={i}
                                  style={{
                                    textAlign: "center",
                                    padding: "10px 6px",
                                    background: "var(--bg-page)",
                                    borderRadius: 8,
                                  }}
                                >
                                  <div style={{ fontSize: 11, color: "var(--text-faint)", marginBottom: 6 }}>
                                    {m.label}
                                  </div>
                                  <DeltaBadge delta={m.delta} invertGood={m.invertGood} />
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                  </>
                ) : (
                  /* Run vs Run stats */
                  <div
                    style={{ background: "var(--bg-card)", borderRadius: 12, padding: 16, marginBottom: 16 }}
                  >
                    <SectionLabel>RUN STATS</SectionLabel>
                    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                      {["distance_km", "pace_min_per_km", "average_hr", "elevation_gain"].map(
                        field => {
                          const labels = {
                            distance_km: "Distance",
                            pace_min_per_km: "Avg Pace",
                            average_hr: "Avg HR",
                            elevation_gain: "Elevation",
                          };
                          const formats = {
                            distance_km: v => `${v} km`,
                            pace_min_per_km: v => (v ? formatPace(1 / (v / 60)) : "—"),
                            average_hr: v => (v ? `${Math.round(v)} bpm` : "—"),
                            elevation_gain: v => `${Math.round(v || 0)} m`,
                          };
                          return (
                            <div
                              key={field}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                padding: "8px 0",
                                borderBottom: "1px solid var(--border-subtle)",
                              }}
                            >
                              <div
                                style={{ width: 80, fontSize: 11, color: "var(--text-subtle)", flexShrink: 0 }}
                              >
                                {labels[field]}
                              </div>
                              <div style={{ flex: 1, display: "flex", gap: 8, flexWrap: "wrap" }}>
                                {selectedRuns.map((run, i) => (
                                  <div
                                    key={i}
                                    style={{ display: "flex", alignItems: "center", gap: 5 }}
                                  >
                                    <div
                                      style={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: "50%",
                                        background: COMPARE_COLORS[i],
                                      }}
                                    />
                                    <span
                                      style={{ fontSize: 13, fontWeight: "bold", color: "var(--text-primary)" }}
                                    >
                                      {formats[field](run[field])}
                                    </span>
                                    {i > 0 && field !== "elevation_gain" && (
                                      <DeltaBadge
                                        delta={pctChange(selectedRuns[0][field], run[field])}
                                        invertGood={
                                          field === "pace_min_per_km" || field === "average_hr"
                                        }
                                      />
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        }
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ══ CHART OVERLAY ══ */}
            {activeSection === "overlay" && (
              <div style={{ background: "var(--bg-card)", borderRadius: 12, padding: 16, marginBottom: 16 }}>
                <SectionLabel>CHART OVERLAY</SectionLabel>
                <p
                  style={{
                    fontSize: 12,
                    color: "var(--text-subtle)",
                    marginTop: 0,
                    marginBottom: 14,
                    fontStyle: "italic",
                  }}
                >
                  Per-second HR, pace, and elevation streams overlaid. X-axis is time elapsed.
                </p>
                <RunOverlayChart
                  selectedRuns={
                    compareMode === "weeks"
                      ? comparedRuns.flatMap((item, i) =>
                          item.runs.map(r => ({ ...r, color: item.color }))
                        )
                      : selectedRuns
                  }
                  stravaId={stravaId}
                />
              </div>
            )}

            {/* ══ SPLIT HEATMAP ══ */}
            {activeSection === "splits" && (
              <div style={{ background: "var(--bg-card)", borderRadius: 12, padding: 16, marginBottom: 16 }}>
                <SectionLabel>SPLIT HEATMAP</SectionLabel>
                <p
                  style={{
                    fontSize: 12,
                    color: "var(--text-subtle)",
                    marginTop: 0,
                    marginBottom: 14,
                    fontStyle: "italic",
                  }}
                >
                  Each km split coloured by HR zone. Reveals pacing strategy, fade points, and
                  improvements over time.
                </p>
                <SplitHeatmap
                  stravaId={stravaId}
                  zoneConfig={zoneConfig}
                  runs={
                    compareMode === "weeks"
                      ? comparedRuns.flatMap((item, i) =>
                          item.runs.map(r => ({ ...r, color: item.color }))
                        )
                      : selectedRuns.map((r, i) => ({ ...r, color: COMPARE_COLORS[i] }))
                  }
                />
              </div>
            )}

            {/* ══ FITNESS RADAR ══ */}
            {activeSection === "radar" && (
              <div style={{ background: "var(--bg-card)", borderRadius: 12, padding: 16, marginBottom: 16 }}>
                <SectionLabel>FITNESS FINGERPRINT</SectionLabel>
                <p
                  style={{
                    fontSize: 12,
                    color: "var(--text-subtle)",
                    marginTop: 0,
                    marginBottom: 14,
                    fontStyle: "italic",
                  }}
                >
                  6-axis radar scored against all your runs. Reveals which dimension of fitness is
                  improving or lagging.
                </p>
                <div
                  style={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 20 }}
                >
                  <RadarChart
                    axes={[
                      { label: "PACE" },
                      { label: "HR EFF." },
                      { label: "Z2 COMP." },
                      { label: "CONSIST." },
                      { label: "VOLUME" },
                      { label: "RHYTHM" },
                    ]}
                    series={(compareMode === "weeks"
                      ? comparedRuns
                      : selectedRuns.map((r, i) => ({
                          ...r,
                          label: COMPARE_LABELS[i],
                          color: COMPARE_COLORS[i],
                          runs: [r],
                        }))
                    ).map((item, i) => {
                      const runs = item.runs || [item];
                      const fp = computeFingerprint(runs, runsWithMeta);
                      return {
                        label: item.label,
                        color: item.color,
                        values: fp ? fp.map(f => f.score) : Array(6).fill(50),
                      };
                    })}
                    size={220}
                  />
                  {/* Legend */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      gap: 8,
                    }}
                  >
                    {(compareMode === "weeks"
                      ? comparedRuns
                      : selectedRuns.map((r, i) => ({
                          ...r,
                          label: COMPARE_LABELS[i],
                          color: COMPARE_COLORS[i],
                          runs: [r],
                        }))
                    ).map((item, i) => {
                      const runs = item.runs || [item];
                      const fp = computeFingerprint(runs, runsWithMeta);
                      return (
                        <div key={i}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              marginBottom: 4,
                            }}
                          >
                            <div
                              style={{
                                width: 12,
                                height: 12,
                                borderRadius: "50%",
                                background: item.color,
                              }}
                            />
                            <span style={{ fontSize: 12, fontWeight: "bold", color: "var(--text-primary)" }}>
                              {compareMode === "weeks"
                                ? `Week ${item.week}`
                                : item.name?.substring(0, 20)}
                            </span>
                          </div>
                          {fp &&
                            fp.map(f => (
                              <div
                                key={f.label}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 6,
                                  paddingLeft: 18,
                                  marginBottom: 2,
                                }}
                              >
                                <div
                                  style={{
                                    width: 40,
                                    height: 4,
                                    borderRadius: 2,
                                    background: "var(--bg-subtle)",
                                    overflow: "hidden",
                                  }}
                                >
                                  <div
                                    style={{
                                      width: `${f.score}%`,
                                      height: "100%",
                                      background: item.color,
                                      borderRadius: 2,
                                    }}
                                  />
                                </div>
                                <span
                                  style={{ fontSize: 9, color: "var(--text-faint)", fontFamily: "monospace" }}
                                >
                                  {f.label} {f.score}
                                </span>
                              </div>
                            ))}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* ══ TRENDS ══ */}
            {activeSection === "trends" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {/* Effort score trend */}
                <div style={{ background: "var(--bg-card)", borderRadius: 12, padding: 16 }}>
                  <SectionLabel>EFFORT SCORE TREND</SectionLabel>
                  <p
                    style={{
                      fontSize: 12,
                      color: "var(--text-subtle)",
                      marginTop: 0,
                      marginBottom: 10,
                      fontStyle: "italic",
                    }}
                  >
                    (HR / MaxHR) × pace. Same pace at lower HR = lower score = improved fitness.
                    Should trend downward.
                  </p>
                  <MultiLineChart
                    series={[
                      {
                        label: "Effort",
                        color: "#E8650A",
                        data: effortTrend.map(r => parseFloat(r.effort)),
                      },
                    ]}
                    height={80}
                    yFormat={v => v.toFixed(2)}
                    xLabels={
                      effortTrend.length > 1
                        ? [
                            `W${effortTrend[0].week}`,
                            `W${effortTrend[Math.floor(effortTrend.length / 2)]?.week}`,
                            `W${effortTrend[effortTrend.length - 1].week}`,
                          ]
                        : []
                    }
                    showDots
                  />
                  {effortTrend.length >= 2 &&
                    (() => {
                      const first = parseFloat(effortTrend[0].effort);
                      const last = parseFloat(effortTrend[effortTrend.length - 1].effort);
                      const delta = (((last - first) / first) * 100).toFixed(1);
                      const improved = last < first;
                      return (
                        <div
                          style={{
                            marginTop: 8,
                            fontSize: 12,
                            color: improved ? "#27AE60" : "#C0392B",
                          }}
                        >
                          {improved ? "↓" : "↑"} {Math.abs(delta)}%{" "}
                          {improved ? "improvement" : "increase"} from first to latest run
                        </div>
                      );
                    })()}
                </div>

                {/* Pace/HR decoupling */}
                <div style={{ background: "var(--bg-card)", borderRadius: 12, padding: 16 }}>
                  <SectionLabel>PACE / HR DECOUPLING</SectionLabel>
                  <p
                    style={{
                      fontSize: 12,
                      color: "var(--text-subtle)",
                      marginTop: 0,
                      marginBottom: 10,
                      fontStyle: "italic",
                    }}
                  >
                    As aerobic fitness builds, pace improves while HR stays flat or drops. Diverging
                    lines = fitness gain.
                  </p>
                  <MultiLineChart
                    series={[
                      {
                        label: "Avg Pace (min/km)",
                        color: "#2E75B6",
                        data: sortedRuns.filter(r => r.pace_min_per_km).map(r => r.pace_min_per_km),
                      },
                      {
                        label: "Avg HR (÷10)",
                        color: "#C0392B",
                        data: sortedRuns.filter(r => r.average_hr).map(r => r.average_hr / 10),
                      },
                    ]}
                    height={90}
                    yFormat={v => v.toFixed(1)}
                    showDots
                  />
                  <div style={{ display: "flex", gap: 16, marginTop: 8 }}>
                    {[
                      { label: "Avg Pace", color: "#2E75B6" },
                      { label: "Avg HR ÷10", color: "#C0392B" },
                    ].map((s, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <div
                          style={{ width: 14, height: 3, background: s.color, borderRadius: 2 }}
                        />
                        <span style={{ fontSize: 10, color: "var(--text-subtle)" }}>{s.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Weekly km trend */}
                <div style={{ background: "var(--bg-card)", borderRadius: 12, padding: 16 }}>
                  <SectionLabel>WEEKLY KM PROGRESSION</SectionLabel>
                  {(() => {
                    const weekKms = Object.entries(
                      runsWithMeta.reduce((acc, r) => {
                        acc[r.week] = (acc[r.week] || 0) + r.distance_km;
                        return acc;
                      }, {})
                    ).sort(([a], [b]) => Number(a) - Number(b));

                    return (
                      <MultiLineChart
                        series={[
                          {
                            label: "Actual km",
                            color: "#27AE60",
                            data: weekKms.map(([, km]) => km),
                          },
                          {
                            label: "Planned km",
                            color: "#E0DDD8",
                            data: weekKms.map(([w]) => WEEK_PLAN[w]?.km || 0),
                          },
                        ]}
                        height={90}
                        yFormat={v => `${v.toFixed(0)}km`}
                        xLabels={
                          weekKms.length > 1
                            ? [
                                `W${weekKms[0][0]}`,
                                `W${weekKms[Math.floor(weekKms.length / 2)][0]}`,
                                `W${weekKms[weekKms.length - 1][0]}`,
                              ]
                            : []
                        }
                        showDots
                      />
                    );
                  })()}
                </div>

                {/* Zone 2 compliance over time */}
                <div style={{ background: "var(--bg-card)", borderRadius: 12, padding: 16 }}>
                  <SectionLabel>ZONE 2 COMPLIANCE TREND</SectionLabel>
                  <p
                    style={{
                      fontSize: 12,
                      color: "var(--text-subtle)",
                      marginTop: 0,
                      marginBottom: 10,
                      fontStyle: "italic",
                    }}
                  >
                    % of runs per week that stayed in Zone 2. Target: consistently above 80%.
                  </p>
                  {(() => {
                    const byWeek = runsWithMeta.reduce((acc, r) => {
                      if (!acc[r.week]) acc[r.week] = { total: 0, z2: 0 };
                      acc[r.week].total++;
                      if (r.zone2_compliant) acc[r.week].z2++;
                      return acc;
                    }, {});
                    const weeks = Object.entries(byWeek).sort(([a], [b]) => Number(a) - Number(b));
                    const pcts = weeks.map(([, v]) => Math.round((v.z2 / v.total) * 100));

                    return (
                      <>
                        <MultiLineChart
                          series={[
                            { label: "Z2 %", color: "#27AE60", data: pcts },
                            { label: "Target 80%", color: "#F0EDE8", data: pcts.map(() => 80) },
                          ]}
                          height={80}
                          yFormat={v => `${Math.round(v)}%`}
                          xLabels={
                            weeks.length > 1
                              ? [`W${weeks[0][0]}`, `W${weeks[weeks.length - 1][0]}`]
                              : []
                          }
                          showDots
                        />
                        {/* Bar version for clarity */}
                        <div
                          style={{
                            display: "flex",
                            gap: 3,
                            marginTop: 12,
                            alignItems: "flex-end",
                            height: 40,
                          }}
                        >
                          {pcts.map((pct, i) => (
                            <div
                              key={i}
                              style={{
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 2,
                              }}
                            >
                              <div
                                style={{
                                  width: "100%",
                                  borderRadius: "2px 2px 0 0",
                                  height: `${pct * 0.4}px`,
                                  background:
                                    pct >= 80 ? "#27AE60" : pct >= 50 ? "#F39C12" : "#C0392B",
                                }}
                              />
                              <span style={{ fontSize: 8, color: "var(--text-faint)", fontFamily: "monospace" }}>
                                W{weeks[i][0]}
                              </span>
                            </div>
                          ))}
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            )}
          </>
        )}

        {/* Empty state */}
        {!hasSelection && (
          <div style={{ textAlign: "center", padding: "40px 20px", color: "var(--text-faint)" }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>📊</div>
            <div style={{ fontSize: 14, fontWeight: "bold", color: "var(--text-subtle)", marginBottom: 6 }}>
              Select at least 2 {compareMode === "weeks" ? "weeks" : "runs"} above
            </div>
            <div style={{ fontSize: 12 }}>
              You can compare up to {MAX_COMPARE} {compareMode === "weeks" ? "weeks" : "runs"} at
              once
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
